/**
 * track:log-jump — "jump view" state. Opening a post that isn't in the live
 * window fetches it with its neighbours into a separate list. The live
 * listener (useTeamLogStream) and `earlierMessages` are never touched, so
 * Back to live is just clearing this state.
 */
import { useCallback, useRef, useState } from "react";
import { fetchMessageWindow } from "../lib/logJump";
import type { TeamLogMessage } from "../types";

export interface LogJump {
  seq: number;
  messages: TeamLogMessage[];
}

export interface LogJumpMissing {
  seq: number;
  first: number | null;
  last: number | null;
}

export interface UseLogJump {
  jump: LogJump | null;
  missing: LogJumpMissing | null;
  error: string | null;
  loading: boolean;
  /** Resolves to the highlighted message's id, or null when nothing opened. */
  jumpTo: (seq: number) => Promise<string | null>;
  backToLive: () => void;
  clearNotice: () => void;
}

export function useLogJump(): UseLogJump {
  const [jump, setJump] = useState<LogJump | null>(null);
  const [missing, setMissing] = useState<LogJumpMissing | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  // Latest request wins: a slow earlier jump must not replace a newer one.
  const requestRef = useRef(0);

  const jumpTo = useCallback(async (seq: number): Promise<string | null> => {
    const request = ++requestRef.current;
    setMissing(null);
    setError(null);
    setLoading(true);
    try {
      const result = await fetchMessageWindow(seq);
      if (request !== requestRef.current) return null;
      if (result.kind === "missing") {
        setJump(null);
        setMissing({ seq, first: result.first, last: result.last });
        return null;
      }
      setJump({ seq, messages: result.messages });
      return result.messages.find((m) => m.sequence === seq)?.id ?? null;
    } catch (err) {
      if (request !== requestRef.current) return null;
      console.error("[Relay] log jump failed:", err);
      setError(`Couldn't open #${seq}. Try again.`);
      return null;
    } finally {
      if (request === requestRef.current) setLoading(false);
    }
  }, []);

  const backToLive = useCallback(() => {
    requestRef.current++;
    setJump(null);
    setMissing(null);
    setError(null);
    setLoading(false);
  }, []);

  const clearNotice = useCallback(() => {
    setMissing(null);
    setError(null);
  }, []);

  return { jump, missing, error, loading, jumpTo, backToLive, clearNotice };
}
